'use client'

import AnimatedText from '@/components/Antonie/Components/animated-text'
import Image from 'next/image'
import React, { useEffect } from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import { Mask } from '@/components/Mask'
import Link from 'next/link'

export const ReviewComponent: React.FC<{
  title: string
}> = ({ title }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  }

  useEffect(() => {
    // Load first HolidayCheck script
    const holidayCheckScript1 = document.createElement('script')
    holidayCheckScript1.src =
      'https://www.holidaycheck.at/widgets?step=code&type=code&options=hotel-1-196134-de_AT-s-b-1-1---0'
    holidayCheckScript1.type = 'text/javascript'
    document.head.appendChild(holidayCheckScript1)

    // Load second HolidayCheck script
    const holidayCheckScript2 = document.createElement('script')
    holidayCheckScript2.src =
      'https://www.holidaycheck.at/widgets?step=code&type=code&options=hotel-1-196134-de_AT-l-w-1-0-1-3-1'
    holidayCheckScript2.type = 'text/javascript'
    document.head.appendChild(holidayCheckScript2)

    // Load TripAdvisor script with a delay to ensure DOM is ready
    const loadTripAdvisor = () => {
      const tripAdvisorScript = document.createElement('script')
      tripAdvisorScript.src =
        'https://www.jscache.com/wejs?wtype=excellent&amp;uniq=296&amp;locationId=2614187&amp;lang=de_AT&amp;display_version=2'
      tripAdvisorScript.async = true
      document.head.appendChild(tripAdvisorScript)
    }

    // Small delay to ensure DOM elements are ready
    setTimeout(loadTripAdvisor, 100)

    return () => {
      // Cleanup scripts on unmount
      if (document.head.contains(holidayCheckScript1)) {
        document.head.removeChild(holidayCheckScript1)
      }
      if (document.head.contains(holidayCheckScript2)) {
        document.head.removeChild(holidayCheckScript2)
      }
      // Note: TripAdvisor script cleanup is handled by the script itself
    }
  }, [])

  return (
    <section className="bg-white py-24">
      <div className="container mx-auto px-6">
        <motion.div ref={ref} className="flex justify-center">
          <Mask width={50} height={50} color="black" />
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="mx-auto max-w-4xl text-center"
        >
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            className="text-lg! mb-8 font-heading font-medium tracking-wide text-gray-900 md:text-4xl"
            classNameText="text-2xl md:text-4xl"
            delay={0.2}
          />

          <motion.div variants={itemVariants} className="mx-auto mb-10 h-px w-16 bg-antonie-red" />

          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center justify-center gap-8 md:flex-col md:gap-12"
          >
            <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:gap-12">
              <div className="holidaycheck">
                <div id="holidaycheck-widget-hotel-1-196134-de_AT-s-b-1-1---0">
                  <div
                    id="holidaycheck_widget_placeholder-196134"
                    style={{ width: '120px', padding: '5px', background: '#0058a2' }}
                  >
                    <Link
                      target="_blank"
                      href="https://www.holidaycheck.at/hi/sporthotel-antonie/29aaba08-a9cc-3d1a-86a4-3f1e5f642d67"
                    >
                      <Image
                        src="https://www.holidaycheck.at/widgets/logo_xlarge.png"
                        alt="Hotels mit Hotelbewertungen bei HolidayCheck"
                        width={120}
                        height={120}
                      />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="tripadvisor">
                <div id="TA_excellent296" className="TA_excellent">
                  <ul id="neRaLEBj" className="TA_links FozEdYbBCd">
                    <li id="9Uw0yCvODr" className="CmMrJD">
                      <Link target="_blank" href="https://www.tripadvisor.at/">
                        <Image
                          src="https://static.tacdn.com/img2/widget/tripadvisor_logo_115x18.gif"
                          alt="TripAdvisor"
                          className="widEXCIMG"
                          id="CDSWIDEXCLOGO"
                          width={115}
                          height={18}
                        />
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bewertung-wrap">
              <div id="holidaycheck-widget-hotel-1-196134-de_AT-l-w-1-0-1-3-1">
                <div
                  id="holidaycheck_widget_placeholder-196134"
                  style={{ width: '120px', padding: '5px', background: '#0058a2' }}
                >
                  <Link
                    target="_blank"
                    href="https://www.holidaycheck.at/hi/sporthotel-antonie/29aaba08-a9cc-3d1a-86a4-3f1e5f642d67"
                  >
                    <Image
                      alt="Hotels mit Hotelbewertungen bei HolidayCheck"
                      src="https://www.holidaycheck.at/widgets/logo_xlarge.png"
                      width="120"
                      height="120"
                    />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

{
  /* <div class="tripadvisor">
<div id="TA_excellent296" class="TA_excellent">
<ul id="neRaLEBj" class="TA_links FozEdYbBCd">
<li id="9Uw0yCvODr" class="CmMrJD">
<a target="_blank" href="https://www.tripadvisor.at/"><img src="https://static.tacdn.com/img2/widget/tripadvisor_logo_115x18.gif" alt="TripAdvisor" class="widEXCIMG" id="CDSWIDEXCLOGO"/></a>
</li>
</ul>
</div>
<script async src="https://www.jscache.com/wejs?wtype=excellent&amp;uniq=296&amp;locationId=2614187&amp;lang=de_AT&amp;display_version=2"></script>
</div> */
}
